import React, { useMemo, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import ChartRenderer, { ChartMissing } from "@/components/assistant/ChartRenderer";
import { ContentErrorBoundary } from "./ErrorBoundary";
import { TickerMentionCard, TweetMentionCard, YouTubeMentionCard } from "./mentions";
import type { ChartSpec, ContentBlock } from "@/types/assistant";
import { parseReportWithCharts } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AssistantMarkdownProps {
  content: string;
  charts?: Map<string, ChartSpec>;
  /** Enable rich content detection (tickers, tweets, youtube) */
  enableRichContent?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Markdown Components with Rich Content
// ─────────────────────────────────────────────────────────────────────────────

const createMarkdownComponents = (enableRichContent: boolean): Components => ({
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-2xl font-bold tracking-tight mb-4 text-white bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-xl font-semibold tracking-tight mt-6 mb-3 text-white flex items-center gap-2">
      <span className="h-5 w-1 rounded-full bg-gradient-to-b from-violet-500 to-fuchsia-500" />
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-lg font-semibold tracking-tight mt-4 mb-2 text-white/90">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => {
    // Parse paragraph content for rich mentions
    if (enableRichContent && typeof children === "string") {
      return <RichParagraph content={children} />;
    }
    // Handle array of children (mixed content)
    if (enableRichContent && Array.isArray(children)) {
      return (
        <p className="mb-4 leading-relaxed text-gray-200">
          {children.map((child, i) => {
            if (typeof child === "string") {
              return <RichTextSpan key={i} text={child} />;
            }
            return <React.Fragment key={i}>{child}</React.Fragment>;
          })}
        </p>
      );
    }
    return <p className="mb-4 leading-relaxed text-gray-200">{children}</p>;
  },
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-none ml-1 mb-4 space-y-3 [&_ul]:mt-2 [&_ul]:ml-4 [&_ul]:space-y-2">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal ml-6 mb-4 space-y-3 text-gray-200 marker:text-gray-500">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="leading-[1.7] text-gray-200 flex items-start gap-3">
      <span className="mt-[0.625em] h-1.5 w-1.5 rounded-full bg-gray-500 flex-shrink-0" />
      <span className="flex-1">{children}</span>
    </li>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto mb-4 rounded-xl border border-gray-700/50">
      <table className="w-full text-left text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-gray-800/60 border-b border-gray-700/50">{children}</thead>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-4 py-3 font-semibold text-gray-200 border-r border-gray-700/30 last:border-r-0">{children}</th>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-gray-700/30">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="hover:bg-gray-800/40 transition-colors">{children}</tr>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-4 py-3 text-gray-300 border-r border-gray-700/30 last:border-r-0">{children}</td>
  ),
  code: ({ children, className }) => {
    // Check if it's a code block (has language class)
    const isBlock = className?.startsWith("language-");
    if (isBlock) {
      return (
        <code className={`block bg-gray-900/80 rounded-lg p-4 text-sm overflow-x-auto font-mono ${className}`}>
          {children}
        </code>
      );
    }
    return (
      <code className="px-1.5 py-0.5 rounded bg-gray-800/80 text-violet-300 text-sm font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="mb-4 rounded-xl border border-gray-700/50 overflow-hidden">
      {children}
    </pre>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-gray-100">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic text-gray-300">{children}</em>
  ),
  a: ({ href, children }) => {
    // Check if it's a special link (Twitter, YouTube)
    if (enableRichContent && href) {
      const twitterMatch = href.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
      if (twitterMatch && twitterMatch[1]) {
        return <TweetMentionCard tweetId={twitterMatch[1]} variant="inline" />;
      }
      
      const youtubeMatch = href.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (youtubeMatch && youtubeMatch[1]) {
        return <YouTubeMentionCard videoId={youtubeMatch[1]} variant="inline" />;
      }
    }
    
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-violet-400 hover:text-violet-300 underline underline-offset-2 decoration-violet-400/30 hover:decoration-violet-300/50 transition-colors"
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-2 border-gray-600 pl-3 my-3 text-gray-400 text-sm [&_p]:mb-1 [&_a]:text-gray-500 [&_a]:no-underline hover:[&_a]:text-gray-300">
      {children}
    </blockquote>
  ),
  hr: () => (
    <hr className="my-6 border-none h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
  ),
});

// ─────────────────────────────────────────────────────────────────────────────
// Rich Content Components
// ─────────────────────────────────────────────────────────────────────────────

/** Parse and render a paragraph with rich content */
const RichParagraph: React.FC<{ content: string }> = ({ content }) => {
  const segments = useMemo(() => parseRichText(content), [content]);

  return (
    <p className="mb-4 leading-relaxed text-gray-200">
      {segments.map((segment, i) => (
        <RichSegment key={i} segment={segment} />
      ))}
    </p>
  );
};

/** Parse and render inline text with rich content */
const RichTextSpan: React.FC<{ text: string }> = ({ text }) => {
  const segments = useMemo(() => parseRichText(text), [text]);

  return (
    <>
      {segments.map((segment, i) => (
        <RichSegment key={i} segment={segment} />
      ))}
    </>
  );
};

type RichSegmentType = 
  | { type: "text"; content: string }
  | { type: "ticker"; ticker: string }
  | { type: "tweet"; tweetId: string; url: string }
  | { type: "youtube"; videoId: string; url: string };

/** Render a single rich segment */
const RichSegment: React.FC<{ segment: RichSegmentType }> = ({ segment }) => {
  switch (segment.type) {
    case "text":
      return <>{segment.content}</>;
    case "ticker":
      return <TickerMention ticker={segment.ticker} />;
    case "tweet":
      return <TweetMentionCard tweetId={segment.tweetId} variant="inline" />;
    case "youtube":
      return <YouTubeMentionCard videoId={segment.videoId} variant="inline" />;
  }
};

/** Interactive ticker mention with hover preview */
const TickerMention: React.FC<{ ticker: string }> = ({ ticker }) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <span 
      className="relative inline-block"
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
    >
      <Link
        to={`/app/tokens/${ticker.toLowerCase()}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 hover:text-violet-200 font-medium text-sm transition-colors border border-violet-500/30"
      >
        ${ticker}
      </Link>
      
      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 left-0 top-full mt-2"
          >
            <TickerMentionCard ticker={ticker} variant="card" />
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

/** Parse text into rich segments */
function parseRichText(text: string): RichSegmentType[] {
  const segments: RichSegmentType[] = [];
  let lastIndex = 0;

  // Combined pattern for all rich content types
  const combinedPattern = /(\$[A-Z]{2,10}\b)|(https?:\/\/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+))|(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))/g;

  let match;
  while ((match = combinedPattern.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }

    if (match[1]) {
      // Ticker match ($XXX)
      segments.push({ type: "ticker", ticker: match[1].slice(1) });
    } else if (match[2] && match[3]) {
      // Twitter match
      segments.push({ type: "tweet", tweetId: match[3], url: match[2] });
    } else if (match[4] && match[5]) {
      // YouTube match
      segments.push({ type: "youtube", videoId: match[5], url: match[4] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", content: text }];
}

// ─────────────────────────────────────────────────────────────────────────────
// Block Renderer (for charts)
// ─────────────────────────────────────────────────────────────────────────────

const BlockRenderer: React.FC<{ 
  block: ContentBlock;
  enableRichContent: boolean;
}> = ({ block, enableRichContent }) => {
  const components = useMemo(
    () => createMarkdownComponents(enableRichContent),
    [enableRichContent]
  );

  if (typeof block === "string") {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {block}
      </ReactMarkdown>
    );
  }

  // Chart placeholder
  if (block.spec) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="my-4"
      >
        <ChartRenderer spec={block.spec} />
      </motion.div>
    );
  }

  // Chart not found
  return <ChartMissing chartId={block.chartId} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AssistantMarkdown: React.FC<AssistantMarkdownProps> = ({
  content,
  charts,
  enableRichContent = true,
}) => {
  const blocks = useMemo(() => {
    const chartMap = charts ?? new Map<string, ChartSpec>();
    return parseReportWithCharts(content, chartMap);
  }, [content, charts]);

  const components = useMemo(
    () => createMarkdownComponents(enableRichContent),
    [enableRichContent]
  );

  // If no charts, just render markdown directly (optimization)
  if (!charts || charts.size === 0) {
    return (
      <div className="assistant-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="assistant-markdown">
      {blocks.map((block, index) => (
        <BlockRenderer 
          key={index} 
          block={block} 
          enableRichContent={enableRichContent}
        />
      ))}
    </div>
  );
};

// Wrap with error boundary to gracefully handle parsing errors
const AssistantMarkdownWithErrorBoundary: React.FC<AssistantMarkdownProps> = (props) => (
  <ContentErrorBoundary rawContent={props.content}>
    <AssistantMarkdown {...props} />
  </ContentErrorBoundary>
);

export default AssistantMarkdownWithErrorBoundary;

